/**
 * Compact plan status badge shown in the header next to the wallet address.
 * - No active subscription: silver "Subscribe" button with shimmer → opens SubscriptionTerminal.
 * - Active subscription: shows plan type + expiration date, click opens SubscriptionTerminal.
 */
import { Calendar, Crown, Gift, Loader2 } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';

export default function PlanBadge({
  onOpenTerminal,
}: {
  onOpenTerminal: () => void;
}) {
  const { subscription, isLoadingSubscription } = useSubscription();
  const isActive = subscription?.status === 'active';
  const isFreePlan = subscription?.plan === 'free';
  const expirationDate = isActive && subscription?.expiresAt
    ? new Date(subscription.expiresAt)
    : null;

  const baseClasses = 'inline-flex items-center justify-center gap-2 px-4 py-2 sm:py-3 text-sm font-semibold rounded-xl min-h-[44px] transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-gray-400/30 whitespace-nowrap cursor-pointer select-none hover:-translate-y-0.5 active:scale-95';

  if (isLoadingSubscription) {
    return (
      <div className={`${baseClasses} bg-neutral-100 dark:bg-neutral-800 text-body opacity-60`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Plan</span>
      </div>
    );
  }

  // ─── Active subscription (click opens terminal for full details) ────────

  if (isActive) {
    return (
      <button
        type="button"
        onClick={onOpenTerminal}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 sm:py-3 text-sm font-semibold rounded-xl min-h-[44px] whitespace-nowrap select-none cursor-pointer bg-gradient-to-r from-primary-600 to-secondary-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 active:scale-95 transition-all duration-300"
        title="View your current plan"
      >
        {isFreePlan ? <Gift className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
        <span className="capitalize text-white">{subscription.plan}</span>
        {expirationDate && (
          <>
            <span className="opacity-70 text-white">·</span>
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline text-white">
              {expirationDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </>
        )}
      </button>
    );
  }

  // ─── No active subscription ─────────────────────────────────────────────

  return (
    <div className="relative">
      <style>{`
        @keyframes silverShimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes silverGlow {
          0%, 100% { box-shadow: 0 0 6px rgba(212,212,216,0.3), 0 2px 8px rgba(161,161,170,0.25); }
          50%      { box-shadow: 0 0 18px rgba(212,212,216,0.55), 0 2px 12px rgba(161,161,170,0.4); }
        }
        .btn-silver-shimmer {
          background-image: linear-gradient(
            110deg,
            #f4f4f5 22%,
            #e4e4e7 38%,
            #d4d4d8 50%,
            #e4e4e7 62%,
            #f4f4f5 78%
          );
          background-size: 200% 100%;
          animation: silverShimmer 8s ease-in-out infinite, silverGlow 8s ease-in-out infinite;
          color: #1c1917;
        }
        .btn-silver-shimmer:hover {
          background-image: linear-gradient(
            110deg,
            #fafafa 22%,
            #e4e4e7 38%,
            #a1a1aa 50%,
            #e4e4e7 62%,
            #fafafa 78%
          );
          background-size: 200% 100%;
          animation: silverShimmer 6s ease-in-out infinite, silverGlow 6s ease-in-out infinite;
        }
      `}</style>
      <button
        type="button"
        onClick={onOpenTerminal}
        className={`${baseClasses} btn-silver-shimmer`}
      >
        <span>Premium</span>
      </button>
    </div>
  );
}
